import { useEffect, useMemo, useRef, useState } from "react";
import { searchPalette, groupResults } from "../../utils/commandPalette";

// Ctrl+K on the Layout Canvas: type to find a tool, condition, drawing, scale or action; ↑ ↓ choose, Enter runs,
// Esc closes. Items that cannot run right now stay in the list, greyed, with the reason. The list and the behavior
// come from utils/commandPalette.js — this is only the box.

export default function CommandPalette({ items, onClose }) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const results = useMemo(() => searchPalette(items, query), [items, query]);
  const grouped = !query.trim();
  const sections = useMemo(() => groupResults(results, { grouped }), [results, grouped]);
  // the order the keyboard walks = the order shown
  const flat = useMemo(() => sections.flatMap(s => s.items), [sections]);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${active}"]`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  const run = (item) => {
    if (!item || item.disabled) return;
    onClose();
    item.run();
  };

  function onKeyDown(e) {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(i => Math.min(flat.length - 1, i + 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive(i => Math.max(0, i - 1)); return; }
    if (e.key === "Enter") { e.preventDefault(); run(flat[active]); }
  }

  let index = -1;
  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,0.45)", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "12vh" }}
    >
      <div role="dialog" aria-label="Command palette" onKeyDown={onKeyDown}
        style={{ width: "min(560px, 92vw)", maxHeight: "70vh", display: "flex", flexDirection: "column", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 10, boxShadow: "0 16px 48px rgba(0,0,0,0.35)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid var(--cs-border)" }}>
          <span aria-hidden="true">🔎</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Find a tool, condition, drawing, scale or action…"
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={flat[active] ? `palette-opt-${active}` : undefined}
            style={{ flex: 1, fontSize: 14, border: "none", outline: "none", background: "transparent", color: "var(--text)" }}
          />
          <kbd style={{ fontSize: 11, padding: "1px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, color: "var(--text3)" }}>Esc</kbd>
        </div>
        <div id="palette-list" role="listbox" ref={listRef} style={{ overflowY: "auto", padding: "4px 0" }}>
          {flat.length === 0 && <div style={{ padding: "16px 14px", fontSize: 13, color: "var(--text3)" }}>Nothing matches “{query}”.</div>}
          {sections.map(sec => (
            <div key={sec.group || "all"}>
              {sec.group && <div style={{ padding: "8px 14px 2px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)" }}>{sec.group}</div>}
              {sec.items.map(item => {
                index += 1;
                const i = index;
                const isActive = i === active;
                return (
                  <div
                    key={item.id}
                    id={`palette-opt-${i}`}
                    role="option"
                    aria-selected={isActive}
                    aria-disabled={!!item.disabled}
                    data-index={i}
                    onMouseMove={() => { if (!isActive) setActive(i); }}
                    onClick={() => run(item)}
                    title={item.disabled || undefined}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 14px", cursor: item.disabled ? "not-allowed" : "pointer", background: isActive ? "rgba(37,99,235,0.12)" : "transparent", opacity: item.disabled ? 0.5 : 1 }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{item.label}</span>
                      {(item.disabled || item.hint) && <span style={{ display: "block", fontSize: 11, color: item.disabled ? "#b45309" : "var(--text3)" }}>{item.disabled || item.hint}</span>}
                    </span>
                    {!grouped && <span style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase" }}>{item.group}</span>}
                    {item.shortcut && <kbd style={{ fontSize: 11, padding: "1px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, color: "var(--text3)" }}>{item.shortcut}</kbd>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div style={{ padding: "6px 12px", borderTop: "1px solid var(--cs-border)", fontSize: 11, color: "var(--text3)", display: "flex", gap: 12 }}>
          <span>↑ ↓ choose</span><span>Enter run</span><span>Esc close</span>
        </div>
      </div>
    </div>
  );
}
